import React, { useState } from 'react';
import { NoteIcon } from 'assets/icons';
import LastUpdateIcon from 'components/lastUpdateIcon/LastUpdateIcon';
import UseContext from 'hooks/useContext';
import CompanyPostItDialog from './CompanyPostItDialog';

// The three colours come from the design and are used nowhere else, so they
// stay arbitrary values rather than entering the Tailwind palette; promote
// them if a second component ever needs the same set.
//
// Only the icon reads this constant — a colour prop, not a class. The title's
// class spells the same value out literally, because Tailwind scans source
// files for complete class strings and would never generate `text-[${...}]`.
export const POST_IT_TITLE_COLOR = '#8D7F3C';

/**
 * The note as the api now holds it, so a copy can redraw itself without
 * reading the company again.
 *
 * The api stamps the date and the author only when the text changed; an
 * unchanged save leaves both as they were. The author is the signed-in
 * admin, and is dropped rather than kept when their name is not known yet:
 * the previous author next to a new date would credit the wrong person.
 */
export const noteAfterSave = (note, content, admin) => {
  const next = { ...note, content };
  if (content === (note?.content ?? '')) return next;

  next.updated_at = new Date().toISOString();
  if (admin?.firstname) {
    next.updated_by = { firstname: admin.firstname, lastname: admin.lastname };
  } else {
    delete next.updated_by;
  }
  return next;
};

/**
 * The company note, rendered identically wherever it appears: the company
 * sheet, the timesheet detail, the mission detail, the contract preview and
 * the invoice detail.
 *
 * Renders nothing when the note is empty — the card is never an empty shell.
 * Every copy is writable once it knows its company: given `companyId`, a click
 * anywhere on the card opens the editor and hands `onSaved` the note as it now
 * stands (`noteAfterSave`). Without it the card is plain text, without a click.
 *
 * `note` is the company's `post_it` sub-document. The author is stamped on it
 * at save time rather than resolved from the change log, because the note is
 * served by several endpoints with their own projections and none of them
 * populates a user.
 *
 * A note pulled from BeSTT has no author — nobody here wrote it — so it is
 * dated by its import and the wording drops the « par … » on its own.
 */
const PostIt = ({ note, companyName, companyId, onSaved, className = '' }) => {
  const { userData } = UseContext() ?? {};
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const text = (note?.content ?? '').trim();
  if (!text) return null;

  const lastUpdate = {
    at: note?.updated_at ?? note?.imported_at,
    by: note?.updated_by,
  };

  const editable = Boolean(companyId);
  const openEditor = () => setIsEditorOpen(true);

  // The dialog is a sibling of the card, not a child: React events bubble
  // along the component tree, portal or not, so a click inside the dialog
  // would otherwise reach the card's own onClick and open it again.
  return (
    <>
      <div
        {...(editable && {
          role: 'button',
          tabIndex: 0,
          onClick: openEditor,
          onKeyDown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            // Space would scroll the page the note sits on.
            event.preventDefault();
            openEditor();
          },
        })}
        className={`bg-[#FFF3B0] border border-solid border-[#F0E18C] rounded p-3 flex gap-2 ${
          editable ? 'cursor-pointer hover:bg-[#FBE99A]' : ''
        } ${className}`}
      >
        <div className={'flex flex-col gap-1 min-w-0 flex-1'}>
          <div className={'flex gap-1.5 items-center text-[#8D7F3C] ft-sm'}>
            <NoteIcon wh={16} color={POST_IT_TITLE_COLOR} />
            <span className={'uppercase'}>
              Post-it{companyName ? ` · ${companyName}` : ''}
            </span>
          </div>
          {/* whitespace-pre-line, not a rich text renderer: the note is stored as
              plain text and its line breaks are the only formatting it carries. */}
          <div className={'text-[#5C4D0A] whitespace-pre-line break-words'}>
            {text}
          </div>
        </div>
        {/* The date moves into the tooltip, as on every card of the section:
            the only thing the card carries on its right. */}
        <div className={'shrink-0 flex items-center gap-2'}>
          <LastUpdateIcon lastUpdate={lastUpdate} color={POST_IT_TITLE_COLOR} />
        </div>
      </div>
      {editable && isEditorOpen && (
        <CompanyPostItDialog
          companyId={companyId}
          onClose={() => setIsEditorOpen(false)}
          onSaved={(content) =>
            onSaved?.(noteAfterSave(note, content, userData))
          }
        />
      )}
    </>
  );
};

export default PostIt;
